import React, {
  useRef,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import {
  Button,
  Grid,
  TextField,
  FormHelperText,
  Typography,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  Stack,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { toast } from "react-toastify";
import { getSocketInstance } from "socket/socket.io";

import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import FileDropzone from "components/Shared/FileDropzone";
import AppSwitch from "components/Shared/AppSwitch";
import SignaturePadComponent from "utils/SignaturePadComponent";
import { useAuth } from "hooks/useAuth";
import { useDOUploadFile } from "hooks";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { getDateTimeAuto } from "utils/dateFormats";
import { CustomCountryPhoneInput } from "components/Shared/CustomCountryPhoneInput";
import {
  useGenerateOTPCodeMutation,
  useGenerateSecurityCodeMutation,
  useValidateCodeMutation,
} from "store/services/shipmentApi";
import { useLazyGetDDLSystemUserListQuery } from "store/services/userApi";
import NCHLQR from "utils/generateNCHLQRCode";
import { useLazyGetDdlShipmentForBranchDeliveryQuery } from "store/services/deliveryApi";

const timestamp = new Date()
  .toISOString()
  .replace(/[-:]/g, "")
  .replace("T", "_")
  .replace(/\..+/, "");

const INITIAL_STATE = {
  shipmentId: [],
  status: "Completed",
  attachments: [],
  documentUrl: "",
  signature: "",
  isActive: true,
  account: "",
  entity: "",
  receiveInBehalf: false,
  receiveBy: "",
  receiverPhone: "",
  deliveryLocation: { lat: null, lon: null },
  securityCode: "",
  securityAttachments: [],
  warehouseId: null,
  responsible: "",
};

const BranchDeliveryForm = ({
  isBusy = false,
  deliveryDetail = null,
  onSubmit = () => { },
}) => {
  const navigate = useNavigate();
  const { viewEntity, isAccountAdmin, isSystemAdmin, loggedInUser } = useAuth();
  const isDriver = loggedInUser?.typeRefSource === "Driver";
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { handleUploadFiles } = useDOUploadFile();
  const signaturePadRef = useRef(null);
  const paymentSuccessHandled = useRef(false);
  const [isPaymentDone, setIsPaymentDone] = useState(false);
  const [paymentResponseData, setPaymentResponseData] = useState(null);
  const [currentTraceId, setCurrentTraceId] = useState(null);
  // Tracks price IDs paid in the current form session. We can't rely
  // solely on the backend's `paymentStatus === "Paid"` flag because
  // the shipments re-fetch takes a few seconds (the websocket
  // messageHandler has to run first). Without this, the user would
  // see "Pay Via NCHL" still enabled for prices they've already paid.
  const [paidPriceIds, setPaidPriceIds] = useState(new Set());
  const nchlQRRef = useRef(null);
  const receiverSetRef = useRef(false);
  const [signatureError, setSignatureError] = useState(false);
  const [isSecurityCodeDisabled, setIsSecurityCodeDisabled] = useState(false);
  const [isOTPCodeDisabled, setIsOTPCodeDisabled] = useState(false);
  const [securityCodeTimer, setSecurityCodeTimer] = useState(0);
  const [otpCodeTimer, setOtpCodeTimer] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);

  const { data: accounts, isLoading: loadingAccounts } =
    useGetDDLAccountListQuery();
  const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();
  const [
    fetchShipments,
    { data: shipments, isLoading: loadingShipments },
  ] = useLazyGetDdlShipmentForBranchDeliveryQuery();
  const [generateOTPCode, { isLoading: loadingGenerateOTPCode }] =
    useGenerateOTPCodeMutation();
  const [generateSecurityCode, { isLoading: loadingGenerateSecurityCode }] =
    useGenerateSecurityCodeMutation();
  const [validateCode, { isLoading: loadingValidateCode }] =
    useValidateCodeMutation();
  const [
    fetchUsers,
    { data: userList, isLoading: loadingUsers, isFetching: fetchingUsers },
  ] = useLazyGetDDLSystemUserListQuery();

  const _mappedUsers = React.useMemo(
    () => userList?.map((el) => ({ label: el?.name, id: el?._id })) || [],
    [userList]
  );

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    trigger,
    formState: { errors, isSubmitted },
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      actualDeliveryTime: getDateTimeAuto(),
      entity: viewEntity?.entity || "",
      account: viewEntity?.entity?.account || "",
      deliveryLocation: { lat: null, lon: null },
    },
  });

  const _watchAccount = useWatch({ control, name: "account" });
  const _watchEntity = useWatch({ control, name: "entity" });
  const _watchWarehouseId = useWatch({ control, name: "warehouseId" });
  const _watchShipmentId = useWatch({ control, name: "shipmentId" });
  const _watchReceiveInBehalf = useWatch({ control, name: "receiveInBehalf" });
  const _watchReceiver = useWatch({ control, name: "receiver" });

  // Define warehouse shipments for dropdown
  // NOTE: declared before handleNCHLPaymentSuccess because the callback
  // depends on unpaidPriceIds (which depends on warehouseShipmentsOptions).
  // JavaScript's lexical scoping means the const reference must be
  // initialized before useCallback's deps array evaluates — otherwise
  // we hit a "can't access lexical declaration 'X' before initialization"
  // ReferenceError.
  const warehouseShipmentsOptions = useMemo(() => {
    return (shipments || []).filter(
      (shipment) => !_watchReceiver || shipment.receiver === _watchReceiver?._id
    );
  }, [shipments, _watchReceiver]);
  const unpaidPriceIds = useMemo(() => {
    if (!_watchShipmentId?.length) return [];
    return _watchShipmentId
      .flatMap((s) => {
        const shipment =
          warehouseShipmentsOptions.find((ws) => ws._id === (s._id || s)) || s;
        return (shipment?.priceItems || [])
          .filter(
            (pi) =>
              pi.paymentStatus !== "Paid" &&
              !paidPriceIds.has(String(pi._id)),
          )
          .map((pi) => pi._id);
      })
      .filter(Boolean);
  }, [_watchShipmentId, warehouseShipmentsOptions, paidPriceIds]);

  const handleNCHLPaymentSuccess = useCallback((response) => {
    if (paymentSuccessHandled.current) return;
    paymentSuccessHandled.current = true;

    let paymentData = null;
    if (response?.paymentData) {
      paymentData = response.paymentData;
    } else if (response?.sessionSrlNo || response?.debitStatus) {
      // Raw NCHL status only — the debit is confirmed but the backend
      // hasn't finished creating the collection record yet (that only
      // happens via the websocket messageHandler). Don't treat this as
      // terminal: there's no redirect data yet, and locking the guard
      // here would silently swallow the real socket event when it
      // arrives. Reset the guard and bail so the follow-up call from
      // the socket can still do the work.
      paymentSuccessHandled.current = false;
      toast.info("Payment confirmed, finalizing collection...");
      return;
    }

    if (paymentData) {
      setPaymentResponseData(paymentData);
      toast.success("NCHL Payment successful!");
      nchlQRRef.current?.handleCloseQR();
      setIsPaymentDone(true);

      // Track paid price IDs locally so the UI updates immediately —
      // the backend's shipments re-fetch won't reflect "Paid" status
      // for a few seconds (the websocket messageHandler has to run
      // first to mark ShipmentPrice as Paid in the DB). Without this,
      // the "Pay Via NCHL" button would stay enabled and the user
      // could pay for the same prices again.
      //
      // The COC socket payload includes `paymentData.priceIds` directly.
      // The DELIVERY socket payload does NOT (see backend
      // nchl.websocket.js socketResponseData — only COC adds priceIds).
      // Fall back to marking the currently-unpaid prices of the selected
      // shipments as paid, since those are exactly the prices the user
      // just paid for (the NCHL dialog was passed unpaidPriceIds).
      const paidIdsFromPayload = Array.isArray(paymentData.priceIds)
        ? paymentData.priceIds.map(String)
        : [];
      const idsToMark =
        paidIdsFromPayload.length > 0
          ? paidIdsFromPayload
          : unpaidPriceIds.map(String);
      if (idsToMark.length > 0) {
        setPaidPriceIds((prev) => {
          const next = new Set(prev);
          idsToMark.forEach((id) => next.add(id));
          return next;
        });
      }

      if (_watchAccount?._id && _watchEntity?._id && _watchWarehouseId?._id) {
        // Extended delay (was 800ms) to give the backend websocket
        // messageHandler time to write the Paid status to the DB before
        // we re-fetch shipments — otherwise the refresh races the DB
        // write and shows stale "Not Paid" status.
        const timer = setTimeout(() => {
          fetchShipments({
            account: _watchAccount._id,
            entity: _watchEntity._id,
            timestamp: Date.now(),
          });
          paymentSuccessHandled.current = false;
        }, 2500);
        return () => clearTimeout(timer);
      } else {
        paymentSuccessHandled.current = false;
      }
    } else {
      paymentSuccessHandled.current = false;
    }
  }, [_watchAccount?._id, _watchEntity?._id, _watchWarehouseId?._id, fetchShipments, unpaidPriceIds]);

  useEffect(() => {
    setIsPaymentDone(false);
    setPaymentResponseData(null);
    // Clear locally-tracked paid IDs so a new shipment starts fresh.
    setPaidPriceIds(new Set());
    // Allow a fresh NCHL payment attempt to re-fire onSuccess now that
    // the selected shipment has changed.
    paymentSuccessHandled.current = false;
  }, [_watchShipmentId]);

  // Socket listener for payment success
  useEffect(() => {
    // Trace-scoped socket listener. The previous implementation listened
    // on the global "payment-success/delivery" event, but the backend
    // routes that event to the OPEN socket.io instance (`/open-socket`
    // path) — which the frontend's socket.io.js does NOT connect to.
    // The frontend connects to the regular instance, which receives
    // "payment-success/delivery-collection/<traceId>". Without this, no
    // delivery-payment socket event ever reaches the form, prices stay
    // "Not Paid", and "Pay Via NCHL" remains clickable for the same
    // shipment.
    if (!currentTraceId) return;
    const socket = getSocketInstance();
    if (!socket) return;

    const uniqueEvent = `payment-success/delivery-collection/${currentTraceId}`;
    const handleSuccess = (response) => {
      if (response.validationTraceId !== currentTraceId) return;
      handleNCHLPaymentSuccess(response);
    };

    socket.on(uniqueEvent, handleSuccess);

    return () => {
      // ONLY remove the listener here. Do NOT reset currentTraceId or
      // paymentSuccessHandled in cleanup — React.StrictMode mounts/
      // unmounts effects twice in dev, and resetting state here would
      // break the listener on the second mount. The _watchShipmentId
      // effect above is the proper reset point.
      socket.off(uniqueEvent, handleSuccess);
    };
  }, [currentTraceId, handleNCHLPaymentSuccess]);

  // Memoize shipmentIds
  const shipmentIds = useMemo(() => {
    return deliveryDetail?.shipmentId
      ? Array.isArray(deliveryDetail.shipmentId)
        ? deliveryDetail.shipmentId
        : [deliveryDetail.shipmentId]
      : [];
  }, [deliveryDetail?.shipmentId]);

  // Define unique receivers for the dropdown
  const receivers = Array.from(
    new Map(
      warehouseShipmentsOptions
        .filter((shipment) => shipment.receiver && shipment.receiverDetails)
        .map((shipment) => [
          shipment.receiver,
          {
            _id: shipment.receiver,
            name: shipment.receiverDetails?.name || "Unknown",
          },
        ])
    ).values()
  );

  // Define selectedShipment and selectedShipmentDetails
  const selectedShipment =
    _watchShipmentId?.length > 0 ? _watchShipmentId[0] : null;
  const selectedShipmentDetails =
    warehouseShipmentsOptions.find((s) => s._id === selectedShipment?._id) ||
    selectedShipment;

  // Define deliverySecurity
  const deliverySecurity =
    selectedShipmentDetails?.deliverySecurity || "Normal";

  useEffect(() => {
    if (!_watchEntity?._id || !_watchAccount?._id || deliveryDetail) return;
    fetchShipments({
      account: _watchAccount._id,
      entity: _watchEntity._id,
    });
  }, [_watchAccount?._id, _watchEntity?._id, fetchShipments, deliveryDetail]);

  // Fetch users when entity changes
  useEffect(() => {
    if (_watchEntity?._id) {
      fetchUsers({ entity: _watchEntity._id });
    }
  }, [_watchEntity, fetchUsers]);

  // Handle deliveryDetail for editing
  useEffect(() => {
    if (!deliveryDetail) return;

    const warehouseIdValue = deliveryDetail?.warehouseId
      ? {
        _id: deliveryDetail.warehouseId._id || deliveryDetail.warehouseId,
        name: deliveryDetail.warehouseId?.name || "Unknown",
        warehouse_id: deliveryDetail.warehouseId?.warehouse_id || "Unknown",
      }
      : null;

    reset({
      ...INITIAL_STATE,
      ...deliveryDetail,
      actualDeliveryTime: deliveryDetail?.actualDeliveryTime
        ? new Date(
          new Date(deliveryDetail.actualDeliveryTime).getTime() +
          (5 * 60 + 45) * 60 * 1000
        )
          .toISOString()
          .slice(0, 16)
        : getDateTimeAuto(),
      shipmentId: shipmentIds.map((shipmentId) => ({
        _id: shipmentId._id || shipmentId,
        shipmentId: shipmentId.shipmentId || "Unknown",
        IsReturning: shipmentId.IsReturning || false,
        status: shipmentId.status || "Warehousing",
        receiver: shipmentId.receiver || null,
        deliverySecurity: shipmentId.deliverySecurity || "Normal",
        uploadedId: shipmentId.uploadedId || [],
        receiverDetails: shipmentId.receiverDetails || null,
        customerDetails: shipmentId.customerDetails || null,
        vendorDetails: shipmentId.vendorDetails || null,
        remainingShipmentAmount: shipmentId.remainingShipmentAmount || 0,
      })),
      account: deliveryDetail?.account || "",
      entity: deliveryDetail?.entity || "",
      deliveryLocation: deliveryDetail?.deliveryLocation || {
        lat: null,
        lon: null,
      },
      securityCode: deliveryDetail?.securityDetails?.value || "",
      securityAttachments: shipmentIds[0]?.uploadedId || [],
      receiver:
        shipmentIds[0]?.receiver && shipmentIds[0]?.receiverDetails
          ? {
            _id: shipmentIds[0].receiver,
            name: shipmentIds[0].receiverDetails?.name || "Unknown",
          }
          : "",
      warehouseId: warehouseIdValue,
      responsible: deliveryDetail?.responsibleUser
        ? {
          id: deliveryDetail.responsibleUser._id,
          label: deliveryDetail.responsibleUser.name,
        }
        : "",
    });

    const accountId =
      deliveryDetail?.account?._id || deliveryDetail?.account || "";
    if (accountId && (isAccountAdmin || isSystemAdmin)) {
      fetchEntities({ account: accountId });
    }

    if (deliveryDetail?.entity) {
      fetchShipments({
        account: accountId,
        entity: deliveryDetail?.entity?._id || deliveryDetail?.entity || "",
      });
    }
  }, [
    deliveryDetail,
    reset,
    fetchEntities,
    fetchShipments,
    isAccountAdmin,
    isSystemAdmin,
    shipmentIds,
  ]);

  // Geolocation for drivers
  useEffect(() => {
    if (isDriver && !deliveryDetail) {
      const getCurrentLocation = () => {
        if (!navigator.geolocation) {
          setValue("deliveryLocation.lat", null);
          setValue("deliveryLocation.lon", null);
          toast.error("Geolocation is not supported by this browser.");
          return;
        }

        navigator.permissions
          .query({ name: "geolocation" })
          .then((permissionStatus) => {
            if (permissionStatus.state === "denied") {
              setValue("deliveryLocation.lat", null);
              setValue("deliveryLocation.lon", null);
              toast.error(
                "Location access denied. Please enable location in your browser settings."
              );
              return;
            }

            navigator.geolocation.getCurrentPosition(
              (position) => {
                const currentLat = position.coords.latitude;
                const currentLon = position.coords.longitude;
                setValue("deliveryLocation.lat", currentLat);
                setValue("deliveryLocation.lon", currentLon);
              },
              (error) => {
                console.error("Failed to fetch location:", error.message);
                setValue("deliveryLocation.lat", null);
                setValue("deliveryLocation.lon", null);
                toast.error(`Failed to fetch location: ${error.message}`);
              },
              { timeout: 10000, maximumAge: 60000 }
            );
          })
          .catch((error) => {
            console.error("Error checking geolocation permission:", error);
            setValue("deliveryLocation.lat", null);
            setValue("deliveryLocation.lon", null);
            toast.error("Error accessing geolocation permissions.");
          });
      };

      getCurrentLocation();
    }
  }, [isDriver, deliveryDetail, setValue]);

  // Countdown effect for Security Code button
  useEffect(() => {
    let timer;
    if (isSecurityCodeDisabled && securityCodeTimer > 0) {
      timer = setInterval(() => {
        setSecurityCodeTimer((prev) => {
          if (prev <= 1) {
            setIsSecurityCodeDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isSecurityCodeDisabled, securityCodeTimer]);

  // Countdown effect for OTP Code button
  useEffect(() => {
    let timer;
    if (isOTPCodeDisabled && otpCodeTimer > 0) {
      timer = setInterval(() => {
        setOtpCodeTimer((prev) => {
          if (prev <= 1) {
            setIsOTPCodeDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isOTPCodeDisabled, otpCodeTimer]);

  useEffect(() => {
    if (_watchShipmentId?.length > 0 && !deliveryDetail && !receiverSetRef.current) {
      const firstShipment = warehouseShipmentsOptions.find(
        (s) => s._id === (_watchShipmentId[0]._id || _watchShipmentId[0])
      );

      if (firstShipment?.receiver && firstShipment?.receiverDetails) {
        setValue("receiver", {
          _id: firstShipment.receiver,
          name: firstShipment.receiverDetails?.name || "Unknown",
        });
        receiverSetRef.current = true;
      }
    } else if (_watchShipmentId?.length === 0) {
      receiverSetRef.current = false;
    }
  }, [_watchShipmentId, warehouseShipmentsOptions, setValue, deliveryDetail]);

  const handleGenerateOTPCode = async () => {
    if (!selectedShipment || isOTPCodeDisabled) return;
    try {
      setIsOTPCodeDisabled(true);
      setOtpCodeTimer(30);
      await generateOTPCode(selectedShipment._id).unwrap();
      toast.success("OTP Code Sent to Mail!");
    } catch (error) {
      setIsOTPCodeDisabled(false);
      setOtpCodeTimer(0);
      toast.error(
        error?.data?.message ?? error?.message ?? "Couldn't generate OTP Code!"
      );
    }
  };

  const getDisplayedAmount = () => {
    if (!_watchShipmentId?.length) return 0;

    return _watchShipmentId.reduce((total, shipment) => {
      const selectedShipment =
        warehouseShipmentsOptions.find(
          (s) => s._id === (shipment._id || shipment)
        ) || shipment;
      const priceItemsTotal =
        selectedShipment?.priceItems?.reduce(
          (sum, item) => {
            // Skip items already paid (either backend-confirmed or
            // locally tracked via paidPriceIds).
            if (
              item.paymentStatus === "Paid" ||
              paidPriceIds.has(String(item._id))
            ) {
              return sum;
            }
            return sum + (item.charge || 0);
          },
          0
        ) || 0;
      return total + priceItemsTotal;
    }, 0);
  };

  // Sum of charges for prices already paid in the current session.
  // Shown in the "Payment successful" indicator next to the NCHL button.
  const getTotalPaid = () => {
    if (!_watchShipmentId?.length) return 0;
    return _watchShipmentId.reduce((total, shipment) => {
      const selectedShipment =
        warehouseShipmentsOptions.find(
          (s) => s._id === (shipment._id || shipment)
        ) || shipment;
      return (
        total +
        (selectedShipment?.priceItems || [])
          .filter((item) => paidPriceIds.has(String(item._id)))
          .reduce((sum, item) => sum + (item.charge || 0), 0)
      );
    }, 0);
  };

  const handleGenerateSecurityCode = async () => {
    if (!selectedShipment || isSecurityCodeDisabled) return;
    try {
      setIsSecurityCodeDisabled(true);
      setSecurityCodeTimer(30);
      await generateSecurityCode(selectedShipment._id).unwrap();
      toast.success("Security Code Sent to Mail!");
    } catch (error) {
      setIsSecurityCodeDisabled(false);
      setSecurityCodeTimer(0);
      toast.error(
        error?.data?.message ??
        error?.message ??
        "Couldn't generate Security Code!"
      );
    }
  };

  const convertDataUrlToFile = (dataUrl, filename) => {
    const arr = dataUrl.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  };

  const handleOnSubmit = async (data) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const signatureDataUrl = signaturePadRef?.current?.getSignature();
      if (!signatureDataUrl) {
        setSignatureError(true);
        return;
      }
      setSignatureError(false);

      const signatureFile = convertDataUrlToFile(
        signatureDataUrl,
        `delivery_signature_${timestamp}.png`
      );

      let securityDetails = { type: deliverySecurity };
      if (["Security Code", "OTP"].includes(deliverySecurity)) {
        if (!data.securityCode) {
          toast.error(`${deliverySecurity} is required.`);
          return;
        }
        try {
          await validateCode({
            shipmentId: selectedShipment._id,
            code: data.securityCode,
            codeType: deliverySecurity,
          }).unwrap();
        } catch (error) {
          toast.error(error?.data?.error || `Invalid ${deliverySecurity}!`);
          return;
        }
        securityDetails.value = data.securityCode;
      } else if (deliverySecurity === "ID") {
        if (!data.securityAttachments?.length) {
          toast.error("ID verification document is required.");
          return;
        }
      }

      const deliveryData = {
        ...data,
        account: data?.account?._id,
        entity: data?.entity?._id,
        shipmentId: data?.shipmentId?.map((s) => s._id),
        warehouseId: data?.warehouseId?._id,
        deliveryLocation: data?.deliveryLocation,
        securityDetails,
        fulfillmentCompletionId:
          data.fulfillmentCompletionId || `FC_${timestamp}`,
        responsibleUser: data?.responsible?.id,
      };

      const [existingAttachments, newAttachments] = data?.attachments?.reduce(
        ([pass, fail], el) =>
          el?.publicId || el?.url
            ? [[...pass, el], fail]
            : [pass, [...fail, el]],
        [[], []]
      );

      const [existingSecurityAttachments, newSecurityAttachments] =
        data?.securityAttachments?.reduce(
          ([pass, fail], el) =>
            el?.publicId || el?.url || el?.secureUrl
              ? [[...pass, el], fail]
              : [pass, [...fail, el]],
          [[], []]
        );

      const uploadPromises = [];

      uploadPromises.push(
        handleUploadFiles([signatureFile], {
          folderName: "DO_FOLDER_NAME.DELIVERY_SIGNATURE",
          entity: viewEntity?.entity?._id,
          account: viewEntity?.entity?.account?._id,
        })
      );

      if (newAttachments?.length) {
        uploadPromises.push(
          handleUploadFiles(newAttachments, {
            folderName: "SHIPMENT_COMPLETION",
            entity: viewEntity?.entity?._id,
            account: viewEntity?.entity?.account?._id,
          })
        );
      }

      if (newSecurityAttachments?.length) {
        uploadPromises.push(
          handleUploadFiles(newSecurityAttachments, {
            folderName: "DELIVERY_SECURITY_ID",
            entity: viewEntity?.entity?._id,
            account: viewEntity?.entity?.account?._id,
          })
        );
      }

      const uploadResults = await Promise.all(uploadPromises);

      const signatureUpload = uploadResults[0];
      const attachmentsUpload = newAttachments?.length
        ? uploadResults[1]
        : null;
      const securityAttachmentsUpload = newSecurityAttachments?.length
        ? uploadResults[newAttachments?.length ? 2 : 1]
        : null;

      deliveryData.signature = signatureUpload.uploadedFiles[0]?.url || "";
      deliveryData.attachments = [
        ...existingAttachments,
        ...(attachmentsUpload?.uploadedFiles || []),
      ].map((attachment) => ({
        name: attachment.name || "",
        type: attachment.type || "",
        size: attachment.size || "",
        publicId: attachment.publicId || "",
        url: attachment.secureUrl || attachment.url || "",
      }));

      let shipmentUpdateData = null;
      if (deliverySecurity === "ID") {
        shipmentUpdateData = {
          shipmentId: selectedShipment._id,
          uploadedId: [
            ...existingSecurityAttachments,
            ...(securityAttachmentsUpload?.uploadedFiles || []),
          ].map((attachment) => ({
            name: attachment.name || "",
            type: attachment.type || "",
            size: attachment.size || "",
            publicId: attachment.publicId || "",
            url: attachment.secureUrl || attachment.url || "",
          })),
        };
      }

      await onSubmit({
        deliveryData,
        shipmentUpdateData,
        paymentData: paymentResponseData,
      });
    } catch (error) {
      console.error("Submission error:", error);
      toast.error(error?.message || "Failed to submit form");
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AppForm loading={isBusy || loadingValidateCode}>
      <AppForm.Content>
        <Grid container spacing={4}>
          {/* Visible "Payment successful" indicator with the amount paid
              for the selected shipment. Placed at the top of the form so
              it's the first thing the user sees after a successful NCHL
              payment — shows immediately when the socket event fires,
              doesn't wait for the shipments re-fetch to confirm Paid
              status in the DB. */}
          {isPaymentDone && paidPriceIds.size > 0 && (
            <Grid item xs={12}>
              <div
                className="inline-flex items-center gap-2 rounded-md border border-green-300 bg-green-50 px-3 py-2 text-sm text-green-800"
                data-testid="nchl-payment-success-indicator"
              >
                <span className="text-base font-semibold leading-none">✓</span>
                <span>
                  Payment successful — Rs{" "}
                  <span className="font-semibold">
                    {getTotalPaid().toFixed(2)}
                  </span>{" "}
                  paid for shipment{" "}
                  <span className="font-semibold">
                    {selectedShipmentDetails?.shipmentId}
                  </span>
                  . Pending amount: Rs{" "}
                  <span className="font-semibold">
                    {getDisplayedAmount().toFixed(2)}
                  </span>
                  .
                </span>
              </div>
            </Grid>
          )}
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item xs={12} md={6}>
              <Controller
                name="account"
                control={control}
                rules={{ required: "Please select an account." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Account"
                    required
                    value={field.value ?? ""}
                    onChange={(_, data) => {
                      field.onChange(data);
                      setValue("entity", "");
                      setValue("warehouseId", null);
                      setValue("shipmentId", []);
                      setValue("receiver", "");
                      setValue("responsible", "");
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={accounts || []}
                    loading={loadingAccounts}
                    loadingText="Loading Account..."
                    disabled
                    helperText={errors?.account?.message}
                    error={!!errors?.account}
                  />
                )}
              />
            </Grid>
          )}
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item xs={12} md={6}>
              <Controller
                name="entity"
                control={control}
                rules={{ required: "Please select an entity." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Entity"
                    value={value ?? ""}
                    onChange={(_, data) => {
                      onChange(data);
                      setValue("warehouseId", null);
                      setValue("shipmentId", []);
                      setValue("receiver", "");
                      setValue("responsible", "");
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={entities || []}
                    loading={loadingEntities}
                    disabled
                    loadingText="Loading Entities..."
                    helperText={errors?.entity?.message}
                    error={!!errors?.entity}
                  />
                )}
              />
            </Grid>
          )}


          <Grid item xs={12} sm={6}>
            <Controller
              name="shipmentId"
              control={control}
              rules={{ required: "Please select at least one Shipment ID." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  multiple
                  fullWidth
                  label="Shipments"
                  required
                  value={value ?? []}
                  onChange={(_, data) => onChange(data)}
                  getOptionLabel={(option) =>
                    option?.shipmentId
                      ? option.IsReturning
                        ? `${option.shipmentId} (R)`
                        : option.shipmentId
                      : ""
                  }
                  isOptionEqualToValue={(option, value) =>
                    option?._id === (value?._id || value)
                  }
                  options={warehouseShipmentsOptions}
                  loading={loadingShipments}
                  loadingText="Loading shipment IDs..."
                  helperText={errors?.shipmentId?.message}
                  error={!!errors?.shipmentId}
                  disabled={!!deliveryDetail}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="receiver"
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Receiver"
                  value={value ?? ""}
                  onChange={(_, data) => {
                    onChange(data);
                    setValue("shipmentId", []);
                  }}
                  getOptionLabel={(option) => option?.name || ""}
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={receivers}
                  loading={loadingShipments}
                  loadingText="Loading receivers..."
                  helperText={
                    errors?.receiver?.message ||
                    (!loadingShipments &&
                      (!receivers || receivers.length === 0)
                      ? "No receivers available"
                      : "")
                  }
                  error={!!errors?.receiver}
                  disabled={
                    !!deliveryDetail || !warehouseShipmentsOptions?.length
                  }
                />
              )}
            />
          </Grid>

          {selectedShipment && (
            <Grid
              item
              xs={12}
              sm={2}
              style={{ display: "flex", justifyContent: "flex-end" }}
            >
              {deliverySecurity === "Security Code" && (
                <Button
                  variant="contained"
                  size="small"
                  disabled={
                    loadingGenerateSecurityCode ||
                    !!deliveryDetail ||
                    isSecurityCodeDisabled
                  }
                  onClick={() => {
                    if (
                      !loadingGenerateSecurityCode &&
                      !isSecurityCodeDisabled
                    ) {
                      handleGenerateSecurityCode();
                    }
                  }}
                >
                  {loadingGenerateSecurityCode
                    ? "Generating..."
                    : isSecurityCodeDisabled
                      ? `Wait ${securityCodeTimer}s`
                      : "Generate Security Code"}
                </Button>
              )}
              {deliverySecurity === "OTP" && (
                <Button
                  variant="contained"
                  disabled={
                    loadingGenerateOTPCode ||
                    !!deliveryDetail ||
                    isOTPCodeDisabled
                  }
                  onClick={() => {
                    if (!loadingGenerateOTPCode && !isOTPCodeDisabled) {
                      handleGenerateOTPCode();
                    }
                  }}
                >
                  {loadingGenerateOTPCode
                    ? "Generating..."
                    : isOTPCodeDisabled
                      ? `Wait ${otpCodeTimer}s`
                      : "Generate OTP Code"}
                </Button>
              )}
            </Grid>
          )}
          {selectedShipment &&
            ["Security Code", "OTP"].includes(deliverySecurity) && (
              <Grid item xs={12} sm={4}>
                <Controller
                  name="securityCode"
                  control={control}
                  rules={{ required: `${deliverySecurity} is required` }}
                  render={({ field }) => (
                    <TextField
                      fullWidth
                      required
                      label={deliverySecurity}
                      {...field}
                      error={!!errors.securityCode}
                      helperText={errors.securityCode?.message}
                      disabled={!!deliveryDetail}
                    />
                  )}
                />
              </Grid>
            )}
          {selectedShipment && deliverySecurity === "ID" && (
            <Grid item xs={12}>
              <FormHelperText>Security Documents</FormHelperText>
              <Controller
                name="securityAttachments"
                control={control}
                rules={{
                  validate: (value) =>
                    value?.length > 0 || "ID document is required.",
                }}
                render={({ field }) => (
                  <FileDropzone
                    multiple
                    maxFiles={2}
                    files={field.value || []}
                    onFileChange={(files) => {
                      setValue("securityAttachments", files);
                      trigger("securityAttachments");
                    }}
                    error={!!errors.securityAttachments}
                    helperText={errors.securityAttachments?.message || ""}
                    showError={isSubmitted}
                    acceptedFileTypes={{
                      "image/*": [".jpeg", ".jpg", ".png"],
                      "application/pdf": [".pdf"],
                    }}
                    disabled={!!deliveryDetail}
                  />
                )}
              />
            </Grid>
          )}
          {_watchShipmentId?.length > 0 && (
            <Grid item xs={12}>
              <Grid container spacing={4}>
                {/* Left side - Shipment Details Card */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ boxShadow: 2, borderRadius: 2, height: '100%' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        Shipment Details
                      </Typography>
                      <div className="mb-4 rounded border border-gray-200 bg-gray-100 p-4 dark:border-gray-700 dark:bg-gray-800">
                        <p className="text-base font-medium">
                          <strong>Total Payment Amount:</strong> Rs.{" "}
                          {getDisplayedAmount().toFixed(2)}
                        </p>
                      </div>
                      {_watchShipmentId.map((shipment, index) => {
                        const selectedShipment =
                          warehouseShipmentsOptions.find(
                            (s) => s._id === (shipment._id || shipment)
                          ) || shipment;

                        // Determine sender details based on customerId or vendor
                        const senderDetails = selectedShipment?.customerId
                          ? selectedShipment.customerDetails
                          : selectedShipment?.vendor
                            ? selectedShipment.vendorDetails
                            : null;

                        const senderType = selectedShipment?.customerId ? "Customer" : "Vendor";

                        // Receiver details
                        const receiverDetails = selectedShipment?.receiverDetails;

                        const validPriceItems =
                          selectedShipment?.priceItems?.filter(
                            (item) => Object.keys(item).length > 0
                          ) || [];

                        return (
                          <Box
                            key={selectedShipment?._id || index}
                            sx={{ mb: 2, pb: 2, borderBottom: "1px solid #eee" }}
                          >
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2">
                                  <strong>
                                    Shipment:{" "}
                                    {selectedShipment?.shipmentId || "Unknown"}
                                  </strong>
                                </Typography>
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                  gutterBottom
                                >
                                  Sender ({senderType})
                                </Typography>
                                {senderDetails ? (
                                  <Stack spacing={0.5}>
                                    <Typography variant="body2">
                                      <strong>Name:</strong>{" "}
                                      {senderDetails.name || senderDetails.shopName || "N/A"}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Phone:</strong>{" "}
                                      {senderDetails.phone || "N/A"}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Address:</strong>{" "}
                                      {senderDetails.address || "N/A"}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Payment Amount:</strong> Rs.{" "}
                                      {(
                                        selectedShipment?.priceItems?.reduce(
                                          (sum, item) => sum + (item.charge || 0),
                                          0
                                        ) || 0
                                      ).toFixed(2)}
                                    </Typography>
                                  </Stack>
                                ) : (
                                  <Typography variant="body2" color="textSecondary">
                                    No sender details available
                                  </Typography>
                                )}
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                  gutterBottom
                                >
                                  Receiver (Customer)
                                </Typography>
                                {receiverDetails ? (
                                  <Stack spacing={0.5}>
                                    <Typography variant="body2">
                                      <strong>Name:</strong>{" "}
                                      {receiverDetails?.name || "N/A"}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Phone:</strong>{" "}
                                      {receiverDetails?.phone || "N/A"}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Address:</strong>{" "}
                                      {receiverDetails?.address || "N/A"}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>E-mail:</strong>{" "}
                                      {receiverDetails?.email || "N/A"}
                                    </Typography>
                                  </Stack>
                                ) : (
                                  <Typography variant="body2" color="textSecondary">
                                    No receiver details available
                                  </Typography>
                                )}
                              </Grid>
                              <Grid item xs={12}>
                                {validPriceItems.length > 0 ? (
                                  <TableContainer component={Paper} sx={{ mt: 1 }}>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Fee Item</TableCell>
                                          <TableCell>Charge</TableCell>
                                          <TableCell>Charge Type</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {validPriceItems.map((priceItem) => (
                                          <TableRow key={priceItem._id}>
                                            <TableCell>
                                              {priceItem.feeItem || "N/A"}
                                            </TableCell>
                                            <TableCell>
                                              Rs. {priceItem.charge?.toFixed(2)}
                                            </TableCell>
                                            <TableCell>
                                              {priceItem.chargeType}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                ) : (
                                  <Typography variant="body2" color="textSecondary">
                                    No unpaid price items available
                                  </Typography>
                                )}
                              </Grid>
                            </Grid>
                          </Box>
                        );
                      })}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Right side - Delivered By field */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="responsible"
                    control={control}
                    rules={{ required: "Please select a Responsible User." }}
                    render={({ field }) => (
                      <BaseAutocomplete
                        fullWidth
                        required
                        label="Delivered By"
                        loading={loadingUsers || fetchingUsers}
                        loadingText="Loading Responsible Users..."
                        options={_mappedUsers}
                        value={field.value || null}
                        onChange={(_, data) => {
                          field.onChange(data);
                        }}
                        getOptionLabel={(option) => option?.label || ""}
                        isOptionEqualToValue={(option, value) =>
                          option?.id === value?.id
                        }
                        error={!!errors?.responsible}
                        helperText={errors?.responsible?.message}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <Controller
              name="actualDeliveryTime"
              control={control}
              rules={{ required: "Actual Delivery Time is required." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  type="datetime-local"
                  label="Actual Delivery Time"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.actualDeliveryTime}
                  helperText={errors.actualDeliveryTime?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="receiveInBehalf"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={field.value}
                      onChange={(e) => {
                        field.onChange(e.target.checked);
                        if (!e.target.checked) {
                          setValue("receiveBy", "");
                          setValue("receiverPhone", "");
                        }
                      }}
                    />
                  }
                  label="Receive In Behalf?"
                />
              )}
            />
          </Grid>
          {_watchReceiveInBehalf && (
            <>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="receiveBy"
                  control={control}
                  rules={{ required: "Please provide receiver Name." }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required
                      fullWidth
                      size="small"
                      label="Receiver Name"
                      placeholder="Enter Receiver Name"
                      error={!!errors?.receiveBy}
                      helperText={errors?.receiveBy?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="receiverPhone"
                  control={control}
                  rules={{
                    required: "Receiver Phone is required",
                    validate: (value) => {
                      const digits = value.replace(/\D/g, "");
                      if (digits.length < 7 || digits.length > 17) {
                        return "Phone number must be between 9 and 16 digits";
                      }
                      return true;
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <CustomCountryPhoneInput
                      placeholder="Receiver Phone Number"
                      label="Receiver Phone"
                      {...field}
                      lockedCountry={{ idd: "+977", cca2: "NP" }}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>
            </>
          )}
          <Grid item xs={12} sm={6}>
            <div
              style={{ display: "flex", alignItems: "stretch", height: "100%" }}
            >
              <SignaturePadComponent
                label="Signature"
                ref={signaturePadRef}
                InputLabelProps={{ shrink: true }}
                width={640}
                height={isMobile ? 390 : 260}
              />
            </div>
            {signatureError && (
              <FormHelperText error>
                Please sign before submitting.
              </FormHelperText>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormHelperText>Delivery Attachments</FormHelperText>
            <Controller
              name="attachments"
              control={control}
              render={({ field }) => (
                <FileDropzone
                  multiple
                  maxFiles={5}
                  files={field?.value || []}
                  onFileChange={(files) => setValue("attachments", files)}
                  disabled={!!deliveryDetail}
                />
              )}
            />
            <div className="mt-4 flex items-center gap-2">
              <Button
                variant="outlined"
                color="success"
                onClick={async () => {
                  const traceId = await nchlQRRef?.current?.handleGenerateQR();
                  if (traceId) setCurrentTraceId(traceId);
                }}
                disabled={
                  isSubmitting ||
                  !selectedShipment ||
                  getDisplayedAmount() <= 0 ||
                  unpaidPriceIds.length === 0 ||
                  !!deliveryDetail ||
                  isPaymentDone
                }
              >
                {getDisplayedAmount() <= 0 || isPaymentDone
                  ? "Payment"
                  : "Pay Via NCHL"}
              </Button>
              <NCHLQR
                ref={nchlQRRef}
                amount={getDisplayedAmount()}
                priceIds={unpaidPriceIds}
                shipmentId={selectedShipmentDetails?.shipmentId || ""}
                onSuccess={handleNCHLPaymentSuccess}
              />
            </div>
          </Grid>
          {deliveryDetail && (
            <Grid item xs={12} sm={6}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <AppSwitch {...field} label="Is Active?" />
                )}
              />
            </Grid>
          )}
        </Grid>
      </AppForm.Content>

      {openConfirmation && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <Card sx={{ p: 3, minWidth: { xs: 280, sm: 400 }, maxWidth: "92vw" }}>
            <Typography variant="h6" gutterBottom>
              Confirm Payment
            </Typography>
            <Typography>
              You are going to pay Rs {getDisplayedAmount().toFixed(2)}. Proceed
              with payment?
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 2,
                gap: 1,
              }}
            >
              <Button
                variant="outlined"
                onClick={() => setOpenConfirmation(false)}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  setOpenConfirmation(false);
                  handleSubmit(handleOnSubmit)();
                }}
              >
                Confirm
              </Button>
            </Box>
          </Card>
        </Box>
      )}

      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => {
            const amount = getDisplayedAmount();
            if (amount > 0) {
              setOpenConfirmation(true);
            } else {
              handleSubmit(handleOnSubmit)();
            }
          }}
          disabled={isBusy || loadingValidateCode || isSubmitting}
        >
          {deliveryDetail ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default BranchDeliveryForm;